import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Checkbox from '@amzn/meridian/checkbox';
import { adminApi, PolicySummary } from '../service/adminApiService';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';

export const UserFormPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user_alias: paramAlias } = useParams<{ user_alias?: string }>();
  const isEdit = Boolean(paramAlias);

  const [alias, setAlias] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [groups, setGroups] = useState<PolicySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Load the full list of groups (first 200 — enough for v1 scale).
        const resp = await adminApi.listGroups({ page_size: 200 });
        if (cancelled) return;
        setGroups(resp.items);
        if (isEdit && paramAlias) {
          const existing = await adminApi.getUser(paramAlias);
          if (cancelled) return;
          setAlias(existing.user_alias);
          setSelectedGroups(new Set(existing.posix_groups));
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load form data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, paramAlias]);

  const toggleGroup = useCallback((name: string) => {
    setSelectedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const canSave = useMemo(
    () => alias.length >= 3 && selectedGroups.size > 0,
    [alias, selectedGroups],
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminApi.upsertUser(alias, { posix_groups: Array.from(selectedGroups).sort() });
      toast.push({
        type: 'success',
        message: isEdit ? `Updated ${alias}` : `Added user ${alias}`,
      });
      navigate('/admin/users');
    } catch (err: any) {
      setError(err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Row alignmentHorizontal="center" spacingInset="500">
        <Loader type="circular" size="large" />
      </Row>
    );
  }

  return (
    <Column spacing="large">
      <PageHeader
        title={isEdit ? `Edit user: ${paramAlias}` : 'Add user'}
        subtitle={isEdit ? 'Change which POSIX groups this user belongs to.' : 'Create a user-group mapping.'}
      />

      {error && <Alert type="error">{error}</Alert>}

      <Section>
      <Column spacing="medium" maxWidth={720}>

      <Column spacing="xsmall">
        <label htmlFor="user-alias">User alias</label>
        <Input
          id="user-alias"
          type="text"
          value={alias}
          onChange={setAlias}
          placeholder="e.g. sllubal"
          disabled={isEdit}
          aria-describedby="user-alias-hint"
        />
        <Text id="user-alias-hint" type="b200" color="secondary">
          Lowercase alphanumeric, 3–32 characters.
        </Text>
      </Column>

      <Column spacing="xsmall">
        <Text type="h100">POSIX groups</Text>
        <Text type="b200" color="secondary">
          Every selected group must already have a policy record. Uncheck to remove.
        </Text>
        <div
          style={{
            maxHeight: '320px',
            overflowY: 'auto',
            border: '1px solid #D5DBDB',
            borderRadius: '4px',
            padding: '0.5rem',
          }}
          role="group"
          aria-label="POSIX group selection"
        >
          {groups.length === 0 && <Text color="secondary">No groups available.</Text>}
          {groups.map((g) => (
            <Checkbox
              key={g.posix_group}
              checked={selectedGroups.has(g.posix_group)}
              onChange={() => toggleGroup(g.posix_group)}
            >
              <Text type="b200">
                {g.posix_group}{' '}
                <span style={{ color: '#6B7A80' }}>
                  — {g.department || '(no dept)'} · {g.operation_count} op(s)
                </span>
              </Text>
            </Checkbox>
          ))}
        </div>
      </Column>

      <Row alignmentHorizontal="end" spacing="small">
        <Button type="tertiary" onClick={() => navigate('/admin/users')}>
          Cancel
        </Button>
        <Button type="primary" onClick={save} disabled={!canSave || saving}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add user'}
        </Button>
      </Row>
      </Column>
      </Section>
    </Column>
  );
};
